// The Mac app's `recording` message (mac-shell protocol v1, desktop.ts):
// the meeting recorder's state, for the app's quit / reload / sleep / sign-out
// guards, its menu bar and its Dock badge. Sent on every change by the
// recorder's lifecycle half (RecorderLifecycle, mounted at the App root, so a
// sign-out never silences it), and never before the page has looked in its
// recorder store once (so `stored` is a number from the first message on).
//
//   {type: "recording", active, phase, startedAt, title, unsent, stored,
//    held, storage, clientRef, meetingId, bound, flushed}
//   phase (recording decisions D1, one meaning each):
//     starting   the microphone is being asked for / the take is registering
//     recording  the microphone is live on this page
//     paused     the microphone is held and the take resumes on this page
//                (the web never sends it: it has no pause of its own)
//     uploading  no microphone; parts stored on this device are being sent
//     finishing  no microphone; a finish is in flight or being retried
//     idle       none of these (a take held for "Carry on" with every part
//                sent is idle with held > 0)
//   active   phase is starting | recording | paused
//   unsent   this account's parts stored here and not yet acknowledged
//   stored   takes in this device's recorder store, EVERY account; null when
//            the store cannot be opened (then storage is "memory")
//   held     this account's takes cut short and waiting for Carry on / Finish
//   storage  "device" (IndexedDB) | "memory" (unsent audio is lost on quit)
//   clientRef, meetingId  the current (or latest unfinished) take's ids
//   bound    true once the page is bound to a workspace AND has taken over
//            every unfinished take of this account it found in the store
//            (false before, after a sign-out, while another page still
//            holds one, or while IndexedDB has not opened and memory stands
//            in, its takes unseen; it is tried again every minute): the app
//            trusts a reloaded page's idle only then
//   flushed  how many flushRecording() calls have had their slice written to
//            the device (incremented, then posted, only once the write has
//            committed; never on a timeout or a failed write)
//
// App → web: window.corvusDesktop.stopRecording() = stop and upload
// everything now (the live take, else every held take of this account), no
// question asked (the app asked); flushRecording() = store what has been
// captured so far, then post a fresh `recording` (the app waits up to 3 s
// for it before a quit, a logout or a shutdown, ending sooner once
// `flushed` goes up).

import { postToDesktop } from "./desktop";

export interface RecordingMessage {
  type: "recording";
  active: boolean;
  phase: "idle" | "starting" | "recording" | "paused" | "uploading" | "finishing";
  startedAt: string | null;
  title: string | null;
  unsent: number;
  stored: number | null;
  held: number;
  storage: "device" | "memory";
  clientRef: string | null;
  meetingId: string | null;
  bound: boolean;
  flushed: number;
}

let last: string | null = null;

/** `recording`, unless the app was already told exactly that (`fresh`: send it anyway, the app is waiting). */
export function announceRecording(message: RecordingMessage, fresh = false): void {
  const key = JSON.stringify(message);
  if (key === last && !fresh) return;
  if (postToDesktop(message)) last = key;
}
